"use client";

import AutoScroll from "embla-carousel-auto-scroll";
import Image from '@/components/ui/Image';
import { Button } from "@/components/ui/button";
import {
  Carousel,
  CarouselContent,
  CarouselItem,
} from "@/components/ui/carousel";
import { ButtonStyleType, SpacingType, sectionVariants } from "@/types";
import { Badge } from "@/components/ui/badge";
import parse from "html-react-parser";
import { SectionWrapper } from "../components/SectionWraper";
import { cn } from "@/lib/utils";
interface Hero1Props {
  darkMode?: boolean;
  variant?: sectionVariants;
  spacing?: SpacingType;
  content?: {
    badge?: string;
    title?: string;
    content?: string;
    hasButton?: boolean;
    buttonText?: string;
    buttonLink?: string;
    buttonStyle?: ButtonStyleType;
    secondaryButton?: boolean;
    secondaryButtonText?: string;
    secondaryButtonLink?: string;
    secondaryButtonStyle?: ButtonStyleType;
  };
  images?: Array<{
    image?: string;
    altText?: string;
  }>;
  attributes?: React.HTMLProps<HTMLElement>;
}

const Hero1 = ({
  darkMode = false,
  variant = "A",
  spacing = 'none',
  content,
  images = [],
  attributes,
}: Hero1Props) => {
  // Ensure we have exactly 8 images by duplicating or truncating
  const getImagesForCarousel = (): Array<{ image: string; altText: string }> => {
    if (images.length === 0) {
      // If no images provided, use default placeholders
      return Array(8).fill({
        image: "/placeholder.svg",
        altText: "placeholder"
      });
    }

    // If we have images, duplicate them until we have 8
    const neededImages: Array<{ image: string; altText: string }> = [];
    while (neededImages.length < 8) {
      neededImages.push(...images.map(img => ({
        image: img.image || "/placeholder.svg",
        altText: img.altText || "placeholder"
      })));
    }
    return neededImages.slice(0, 8);
  };

  const carouselImages = getImagesForCarousel();
  const gradientBackground = cn(
    "absolute inset-0 z-10",
    variant === 'A' 
      ? "from-background from-0% to-transparent to-100%" 
      : variant === 'B' 
        ? "from-muted from-0% to-transparent to-100%"
        : "from-background from-0% to-transparent to-100%"
  );

  return (
    <SectionWrapper
      spacing={spacing}
      variant={variant}
      darkMode={darkMode}
      heroSection={true}
      componentName="hero1"
      attributes={attributes}
      usedInPageNav={false}
      pageNavTitle="Hero 1"
    > 
      <div className="container">
        <div className="grid items-center gap-x-16 lg:grid-cols-[55%_45%] text-center lg:text-left">
          <div className="py-10">
            {content?.badge && (
              <Badge variant="outline" bold className="mb-6">
                {content.badge}
              </Badge>
            )}
            {content?.title && (
              <h1 className="mb-8">
                {parse(content.title)}
              </h1>
            )}
            {content?.content && (
              <div className="mx-auto text-muted-foreground paragraph-lg">
                {parse(content.content)}
              </div>
            )}
            {content?.hasButton && (
              <div className="mt-8 flex flex-col gap-2 sm:flex-row justify-center lg:justify-start">
                <Button 
                  size="lg" 
                  variant={content?.buttonStyle || "primary"}
                  className="w-full gap-2 sm:w-auto"
                  asChild
                >
                  <a href={content?.buttonLink}>
                    {content?.buttonText}
                  </a>
                </Button>
                {content?.secondaryButton && (
                  <Button
                    variant={content?.secondaryButtonStyle || "outline"}
                    size="lg"
                    className="w-full gap-2 sm:w-auto"
                    asChild
                  >
                    <a href={content?.secondaryButtonLink}>
                      {content?.secondaryButtonText}
                    </a>
                  </Button>
                )}
              </div>
            )}  
          </div>
          <div className={cn("flex flex-col gap-8 lg:hidden overflow-hidden", spacing === 'none' ? 'pb-14' : 'lg:pb-0')}>
            <Carousel
              opts={{
                loop: true,
              }}
              plugins={[
                AutoScroll({
                  speed: 0.8,
                }),
              ]}
              className="-mx-30 relative"
            >
              <div className={cn(gradientBackground, "bg-gradient-to-l bottom-0 right-26 md:right-16 left-auto top-0 w-[20%]")}></div>
              <CarouselContent className="max-h-[350px]">
                {carouselImages.slice(0, 8).map((image, index) => (
                  <CarouselItem key={index} className="max-w-75 pl-8">
                    <Image
                      src={image.image}
                      alt={image.altText || `Image ${index + 1}`}
                      title={image.altText || `Image ${index + 1}`}
                      width={300}
                      height={300}
                      className="rounded-2xl object-cover"
                      priority={index === 0}
                      fetchPriority={index === 0 ? 'high' : 'auto'}
                    />
                  </CarouselItem>
                ))}
              </CarouselContent>
              <div className={cn(gradientBackground, "bg-gradient-to-r bottom-0 left-26 md:left-16 right-auto top-0 w-[20%]")}></div>
            </Carousel>
            {/* <Carousel
              opts={{
                loop: true,
              }}
              plugins={[
                AutoScroll({
                  speed: 0.8,
                  direction: "backward",
                  stopOnInteraction: false,
                 
                }),
              ]}
              className="-mx-30 relative"
            >
              <div className={cn(gradientBackground, "bg-gradient-to-l bottom-0 right-26 md:right-16 left-auto top-0 w-[20%]")}></div>
              <CarouselContent className="max-h-[350px]">
                {carouselImages.slice(4, 8).map((image, index) => (
                  <CarouselItem key={index} className="max-w-75 pl-8">
                    <Image
                      src={image.image}
                      alt={image.altText || `Image ${index + 5}`}
                      title={image.altText || `Image ${index + 5}`}
                      width={300}
                      height={300}
                      className="rounded-2xl object-cover"
                    />
                  </CarouselItem>
                ))}
              </CarouselContent>
              <div className={cn(gradientBackground, "bg-gradient-to-r bottom-0 left-26 md:left-16 right-auto top-0 w-[20%]")}></div>
            </Carousel> */}
          </div>
          <div className="hidden grid-cols-2 gap-8 lg:grid">
            <Carousel
              opts={{
                loop: true,
              }}
              plugins={[
                AutoScroll({
                  speed: 0.8,
                }),
              ]}
              orientation="vertical"
            >
              <div className={cn(gradientBackground, "bg-gradient-to-b top-0 left-0 right-0 bottom-auto h-[20%]")}></div>
              <CarouselContent className="max-h-[800px]">
                {carouselImages.slice(0, 4).map((image, index) => (
                  <CarouselItem key={index} className="pt-8">
                    <Image
                      src={image.image}
                      alt={image.altText || `Image ${index + 1}`}
                      title={image.altText || `Image ${index + 1}`}
                      width={330}
                      height={330}
                      className="rounded-2xl object-cover"
                      priority={index === 0}
                      fetchPriority={index === 0 ? 'high' : 'auto'}
                    />
                  </CarouselItem>
                ))}
              </CarouselContent>
              <div className={cn(gradientBackground, "bg-gradient-to-t bottom-0 left-0 right-0 top-auto h-[20%]")}></div>
            </Carousel>
            <Carousel
              opts={{
                loop: true,
              }}
              plugins={[
                AutoScroll({
                  speed: 0.8,
                  direction: "backward",
                }),
              ]}
              orientation="vertical"
            >
              <div className={cn(gradientBackground, "bg-gradient-to-b top-0 left-0 right-0 bottom-auto h-[20%]")}></div>
              <CarouselContent className="max-h-[800px]">
                {carouselImages.slice(4, 8).map((image, index) => (
                  <CarouselItem key={index} className="pt-8">
                    <Image
                      src={image.image}
                      alt={image.altText || `Image ${index + 5}`}
                      title={image.altText || `Image ${index + 5}`}
                      width={330}
                      height={330}
                      className="rounded-2xl object-cover"
                      priority={index === 0}
                      fetchPriority={index === 0 ? 'high' : 'auto'}
                    />
                  </CarouselItem>
                ))}
              </CarouselContent>
              <div className={cn(gradientBackground, "bg-gradient-to-t bottom-0 left-0 right-0 top-auto h-[20%]")}></div>
            </Carousel>
          </div>
        </div>
      </div>
    </SectionWrapper>
  );
};

export { Hero1 };



